<template>
  <v-btn
    v-bind="$attrs"
    :variant="variant"
    class="no-text-decoration"
    :class="{
      'text-primary': cancel,
      'action-btn': isActionBtn,
      'danger-btn': danger,
      'bg-secondary': !text && secondary,
      'bg-primary': !text && (secondary || primary)
    }"
    exact
    :slim="false"
  >
    <v-icon
      v-bind="sizeProps"
      :size="iconSize"
      :color="iconColor"
      :start="!!$slots.default"
      v-if="iconName"
      :class="{ rotate: spinIcon }"
      :dynamic-icon="dynamicIcon"
    >
      {{ iconName }}
    </v-icon>
    <slot />
  </v-btn>
</template>

<script>
/**
 * Sofy button component.
 *
 * Extends Vuetify v-btn.
 * Supports primary and secondary types.

 * ## Slots
 *
 * Name | Scope | Description
 * -----|-------|------------
 * default | n/a | The button content/text
 *
 * @example
 * ```
 * <s-btn
 *     secondary
 *     prepend-icon="mdi:mdi-plus"
 *     :to="{ name: 'admin-endpoint-servers-add-server' }"
 * >
 *     Add new endpoint server
 * </s-btn>
 *```
 * @component common/components/s-btn
 *
 * @vueprop {String} [iconName=name] Works the same as v-icon. Icons is `small`.
 * @vueprop {Boolean} [spinIcon=false] True means spin the icon (as a load, for example).
 * @vueprop {Boolean} [secondary=false] Button will have these attributes: `outlined color="bg-primary"`.
 * @vueprop {Boolean} [primary=false] Button will have these attributes: `color="bg-primary"`.
 * @vueprop {Boolean} [cancel=false] Button will have these attributes: `text class="text-primary"`.
 * @vueprop {Boolean} [text=false] Same as Vuetify v-btn
 * @vueprop {Boolean} [isActionBtn=false] Button will have these attributes: `class="action-btn"`.
 */
export default {
  props: {
    /**
     * Works the same as v-icon. Icons is `small`.
     */
    iconName: String,
    iconColor: String,
    iconSize: { type: String, default: 'small' },
    spinIcon: Boolean,
    smallIcon: { type: Boolean, default: true },
    secondary: Boolean,
    primary: Boolean,
    cancel: Boolean,
    isActionBtn: { type: Boolean, default: false },
    danger: Boolean,
    text: { type: Boolean, default: false },
    dynamicIcon: { type: Boolean, default: false }
  },
  computed: {
    // This computed proprty filters out all the size related properties passed to the component
    sizeProps() {
      const sizeKeys = ['x-large', 'large', 'small', 'x-small'];
      return Object.keys(this.$attrs)
        .filter((key) => sizeKeys.includes(key))
        .reduce((sizeAttrs, curKey) => ({ ...sizeAttrs, [curKey]: this.$attrs[curKey] }), {});
    },
    variant() {
      if (this.text) {
        return 'text';
      }
      return this.cancel || this.secondary ? 'outlined' : 'elevated';
    }
  }
};
</script>

<style lang="scss" scoped>
.action-btn {
  height: 25px;
  min-width: 66.34px;
  border-radius: 1px;
}

.danger-btn {
  background-color: rgb(var(--v-theme-error)) !important;
  color: rgb(var(--v-theme-lightThemeText));
}
</style>
